import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AdminRoute, UserRoute } from "./components/AuthGate";
import { Layout } from "./components/Layout";

/** Tiny indirection so the redirect can sit inside an ``<AdminRoute>``
 *  guard without reimplementing the auth gate. ``replace`` keeps the
 *  browser history clean — pressing Back from /mutes shouldn't bounce
 *  back through /dex-mutes. */
function DexMutesRedirect() {
  return <Navigate to="/mutes" replace />;
}

const DashboardPage = lazy(() => import("./pages/Dashboard"));
const SpreadsPage = lazy(() => import("./pages/Spreads"));
const OrderbooksPage = lazy(() => import("./pages/Orderbooks"));
const DexDumpPage = lazy(() => import("./pages/DexDump"));
const CexDumpPage = lazy(() => import("./pages/CexDump"));
const WallsPage = lazy(() => import("./pages/Walls"));
const ListingsPage = lazy(() => import("./pages/Listings"));
const DwChangesPage = lazy(() => import("./pages/DwChanges"));
// Premarket page — landing for the E6-E8 pre-IPO / tokenised-equity
// detection pipeline that currently emits to CHANNEL_PREMARKET in
// Telegram. Placeholder for now (links operator to the Telegram
// channel); will host the proper dashboard surface in a follow-up
// (per-group history, raw vs. normalised price toggle, ungrouped
// ratio drift indicator).
const PremarketPage = lazy(() => import("./pages/Premarket"));
const FundingPage = lazy(() => import("./pages/Funding"));
// Charts hub (#68) — in-app funding-per-exchange + spread/price charts for a
// token. Lazy because lightweight-charts is ~50 KB gzipped.
const ChartsPage = lazy(() => import("./pages/Charts"));
const TrackerPage = lazy(() => import("./pages/TrackerState"));
const MutesPage = lazy(() => import("./pages/Mutes"));
// 3 new pages migrated from the legacy static dashboard (token.html /
// debug.html / tax_cache.html). Kept lazy so the bundle for
// ``/spreads`` and ``/orderbooks`` (the hot pages) stays small.
//
// Standalone ``/dex-mutes`` (formerly ``DexMutesPage``) was folded
// into ``Mutes::DexPanel`` 2026-05-01 — same backend, same query
// cache key, was duplicating UX. The route below 301-style redirects
// to ``/mutes`` for any old bookmark.
const TokenInfoPage = lazy(() => import("./pages/TokenInfo"));
const DebugPage = lazy(() => import("./pages/Debug"));
const TaxCachePage = lazy(() => import("./pages/TaxCache"));
// Per-spread chart page — opened in a new tab from the LineChart icon on
// each row in Spreads / MainSpreadsPanel. Lazy because lightweight-charts
// is ~50 KB gzipped and only needed when a chart is actually opened.
const ChartPage = lazy(() => import("./pages/Chart"));
// Per-instrument price chart — opened in a new tab from the chart icon on each
// Orderbooks row. Candlestick OHLC of one instrument's USD mid (#17 follow-up).
const PriceChartPage = lazy(() => import("./pages/PriceChart"));
// Auth pages — kept lazy so anonymous landing on /login doesn't load
// the entire Spreads/Orderbooks bundles.
const LoginPage = lazy(() => import("./pages/Login"));
const ProfilePage = lazy(() => import("./pages/Profile"));

function Fallback() {
  return (
    <div className="flex h-full items-center justify-center text-text3">
      Loading…
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      {/* Login is rendered WITHOUT the standard Layout (no top nav)
          so the user lands on a clean sign-in card. */}
      <Route
        path="login"
        element={
          <Suspense fallback={<Fallback />}>
            <LoginPage />
          </Suspense>
        }
      />

      <Route element={<Layout />}>
        <Route
          index
          element={
            <Suspense fallback={<Fallback />}>
              <DashboardPage />
            </Suspense>
          }
        />
        <Route
          path="spreads"
          element={
            <Suspense fallback={<Fallback />}>
              <SpreadsPage />
            </Suspense>
          }
        />
        <Route
          path="premarket"
          element={
            <Suspense fallback={<Fallback />}>
              <PremarketPage />
            </Suspense>
          }
        />
        <Route
          path="funding"
          element={
            <Suspense fallback={<Fallback />}>
              <FundingPage />
            </Suspense>
          }
        />
        <Route
          path="charts"
          element={
            <Suspense fallback={<Fallback />}>
              <ChartsPage />
            </Suspense>
          }
        />
        <Route
          path="orderbooks"
          element={
            <AdminRoute>
              <Suspense fallback={<Fallback />}>
                <OrderbooksPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="dex-dump"
          element={
            <Suspense fallback={<Fallback />}>
              <DexDumpPage />
            </Suspense>
          }
        />
        <Route
          path="cex-dump"
          element={
            <Suspense fallback={<Fallback />}>
              <CexDumpPage />
            </Suspense>
          }
        />
        <Route
          path="walls"
          element={
            <Suspense fallback={<Fallback />}>
              <WallsPage />
            </Suspense>
          }
        />
        <Route
          path="listings"
          element={
            <Suspense fallback={<Fallback />}>
              <ListingsPage />
            </Suspense>
          }
        />
        <Route
          path="dw-changes"
          element={
            <Suspense fallback={<Fallback />}>
              <DwChangesPage />
            </Suspense>
          }
        />
        {/* Admin-only pages: tracker / mutes / debug / tax-cache.
            ``<AdminRoute>`` redirects anon to /login and shows a 403
            splash for logged-in non-admins. Backend endpoints they
            consume (``/api/mute``, ``/api/dex-mutes``, etc.) are
            independently gated by ``Depends(require_admin)``. */}
        <Route
          path="tracker"
          element={
            <AdminRoute>
              <Suspense fallback={<Fallback />}>
                <TrackerPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="mutes"
          element={
            <AdminRoute>
              <Suspense fallback={<Fallback />}>
                <MutesPage />
              </Suspense>
            </AdminRoute>
          }
        />
        {/* Legacy ``/dex-mutes`` URL → redirect into the unified Mutes
            page so old bookmarks still land somewhere useful (the DEX
            tab is right there). React-router-dom v6 supports a Navigate
            element instead of a custom component. */}
        <Route
          path="dex-mutes"
          element={
            <AdminRoute>
              <DexMutesRedirect />
            </AdminRoute>
          }
        />
        <Route
          path="token-info"
          element={
            <Suspense fallback={<Fallback />}>
              <TokenInfoPage />
            </Suspense>
          }
        />
        <Route
          path="debug"
          element={
            <AdminRoute>
              <Suspense fallback={<Fallback />}>
                <DebugPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="tax-cache"
          element={
            <AdminRoute>
              <Suspense fallback={<Fallback />}>
                <TaxCachePage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="chart"
          element={
            <Suspense fallback={<Fallback />}>
              <ChartPage />
            </Suspense>
          }
        />
        <Route
          path="price-chart"
          element={
            <Suspense fallback={<Fallback />}>
              <PriceChartPage />
            </Suspense>
          }
        />
        {/* Profile requires authentication. Anon → redirected to
            /login?next=/profile. */}
        <Route
          path="profile"
          element={
            <UserRoute>
              <Suspense fallback={<Fallback />}>
                <ProfilePage />
              </Suspense>
            </UserRoute>
          }
        />
      </Route>
    </Routes>
  );
}
